import React from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    useTheme,
    Box,
} from "@mui/material";

export default function NCMathTable({ amount, price, showBoxCalcs }) {
    const theme = useTheme();

      // Helper to safely convert string → number (use when needed)
  function toNumber(input) {
    if (input === "" || input === null) return 0;
    const num = parseFloat(input);
    return isNaN(num) ? 0 : num;
  }


    // Hardcoded rows (rows 2–6 from your spreadsheet, replace with real data)
    const staticRows = [
        {
            id: 2,
            "NC Amount": 500,
            "Price": 5,
            "NC Per Dollar": (500 / 5),
            "Equivalent # of NC Cards": (500 / 750),
            "Price Per Card": 5 / (500 / 750),
            "Trendsetter Caps": 0,
            "Boxes": 0,
            "Boxes - NC Card (Rounded)": 0,
            "Box Difference": 0,
            "Cost of Box Difference (GBC)": 0,
            "Cost of Box Difference (BFGBC)": 0,
        },
        {
            id: 3,
            "NC Amount": '1,000',
            "Price": 10,
            "NC Per Dollar": (1000 / 10),
            "Equivalent # of NC Cards": (1000 / 750),
            "Price Per Card": 10 / (1000 / 750),
            "Trendsetter Caps": 0,
            "Boxes": 2,
            "Boxes - NC Card (Rounded)": 2,
            "Box Difference": 0,
            "Cost of Box Difference (GBC)": 0,
            "Cost of Box Difference (BFGBC)": 0,
        },
        {
            id: 4,
            "NC Amount": '2,200',
            "Price": 20,
            "NC Per Dollar": (2200 / 20),
            "Equivalent # of NC Cards": (2200 / 750),
            "Price Per Card": 20 / (2200 / 750),
            "Trendsetter Caps": 1,
            "Boxes": 4,
            "Boxes - NC Card (Rounded)": 4,
            "Box Difference": 0,
            "Cost of Box Difference (GBC)": 0,
            "Cost of Box Difference (BFGBC)": 0,
        },
        {
            id: 5,
            "NC Amount": '6,000',
            "Price": 50,
            "NC Per Dollar": (6000 / 50),
            "Equivalent # of NC Cards": (6000 / 750),
            "Price Per Card": 50 / (6000 / 750),
            "Trendsetter Caps": 2,
            "Boxes": 5,
            "Boxes - NC Card (Rounded)": 16,
            "Box Difference": 11,
            "Cost of Box Difference (GBC)": "1000-1200NC",
            "Cost of Box Difference (BFGBC)": "300-400NC",
        },
        {
            id: 6,
            "NC Amount": '14,000',
            "Price": 100,
            "NC Per Dollar": (14000 / 100),
            "Equivalent # of NC Cards": (14000 / 750),
            "Price Per Card": 100 / (14000 / 750),
            "Trendsetter Caps": 3,
            "Boxes": 8,
            "Boxes - NC Card (Rounded)": 36,
            "Box Difference": 28,
            "Cost of Box Difference (GBC)": "2800NC",
            "Cost of Box Difference (BFGBC)": "700NC",
        },
    ];

    // Dynamic row (row 7)
    const C = amount > 0 && price > 0 ? amount / price : 0;
    const D = amount / 750 || 0;
    const E = D > 0 ? price / D : 0;
    const G = (D * 2).toFixed(0);
    const H = (D * 2).toFixed(0);

    const dynamicRow = {
        id: 7,
        "NC Amount": toNumber(amount),
        "Price": toNumber(price),
        "NC Per Dollar": C,
        "Equivalent # of NC Cards": D,
        "Price Per Card": E,
        "Trendsetter Caps": 0,
        Boxes: G,
        "Boxes - NC Card (Rounded)": H,
        "Box Difference": 0,
        "Cost of Box Difference (GBC)": 0,
        "Cost of Box Difference (BFGBC)": 0,
    };

    const allRows = [...staticRows, dynamicRow];

    // Column definitions
    const columns = [
        "NC Amount",
        "Price",
        "NC Per Dollar",
        "Equivalent # of NC Cards",
        "Price Per Card",
        "Trendsetter Caps",
        "Boxes",
        "Boxes - NC Card (Rounded)",
        "Box Difference",
        "Cost of Box Difference (GBC)",
        "Cost of Box Difference (BFGBC)",
    ];

    const visibleColumns = showBoxCalcs
        ? columns
        : columns.filter(
            (col) =>
                ![
                    "Boxes - NC Card (Rounded)",
                    "Box Difference",
                    "Cost of Box Difference (GBC)",
                    "Cost of Box Difference (BFGBC)",
                ].includes(col)
        );


    const displayCell = (col, val) => {
        if (col === "Price" || col === "Price Per Card") {
            return `$${val.toFixed(2)}`
        }
        if (Number.isFinite(val)) {
            if (col === "NC Per Dollar" ||
                col === "Equivalent # of NC Cards" ||
                col === "Price Per Card") {
                return val.toFixed(2)
            }
        }
        return val
    }

    return (
        <Box sx={{
            width: '100%',
            overflow: 'hidden',
            display: 'flex',
            justifyContent: 'center',
        }}>
            <TableContainer
                component={Paper}
                elevation={3}
                sx={{
                    borderRadius: 2,
                    width: '100%',
                    // Simple responsive maxWidth that doesn't fight with GuideLayout
                    maxWidth: {
                        xs: '80vw', // Mobile: use full available width from GuideLayout
                        sm: '85vw', // Tablet: use full available width from GuideLayout
                        md: '60vw', // Desktop: fixed max width that fits in content area
                        lg: '850px',  // Large screens: use 90% for better utilization
                    },
                    overflowX: 'auto',
                    overflowY: 'visible',
                    '&::-webkit-scrollbar': {
                        height: 8,
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: theme.palette.grey[200],
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: theme.palette.grey[400],
                        borderRadius: 4,
                    },
                }}
            >
                <Table
                    stickyHeader
                    size="small"
                    sx={{
                        // Dynamic width calculation that's always larger than container to force scroll
                        minWidth: showBoxCalcs
                            ? Math.max(visibleColumns.length * 120, 900) // Ensure minimum width for all columns
                            : Math.max(visibleColumns.length * 120, 600), // Smaller for fewer columns
                        tableLayout: 'fixed',
                        // Don't set a fixed width - let minWidth handle it
                    }}
                >
                    <TableHead>
                        <TableRow sx={{
                            '& > th': {
                                borderRight: `1px solid ${theme.palette.primary.dark}`,
                                '&:last-child': {
                                    borderRight: 'none'
                                }
                            }
                        }}>
                            {visibleColumns.map((col) => (
                                <TableCell
                                    key={col}
                                    align="center"
                                    sx={{
                                        backgroundColor: theme.palette.primary.main,
                                        color: theme.palette.primary.contrastText,
                                        fontWeight: "bold",
                                        whiteSpace: "normal",
                                        wordWrap: "break-word",
                                        lineHeight: 1.2,
                                        py: 1,
                                        // Responsive column widths
                                        minWidth: { xs: 100, sm: 120 },
                                        maxWidth: { xs: 140, sm: 160 },
                                        maxHeight: '2.4em',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 100,
                                        border: `1px solid ${theme.palette.primary.main}`,
                                        fontSize: { xs: '0.7rem', sm: '0.875rem' }, // Smaller text on mobile
                                    }}
                                >
                                    {col}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {allRows.map((row, rowIndex) => (
                            <TableRow
                                key={row.id}
                                sx={{
                                    backgroundColor:
                                        rowIndex % 2 === 0
                                            ? theme.palette.action.hover
                                            : "inherit",
                                    "&:hover": {
                                        backgroundColor: theme.palette.action.selected,
                                    },
                                }}
                            >
                                {visibleColumns.map((col) => (
                                    <TableCell
                                        key={col}
                                        align="center" // Changed to center as requested
                                        sx={{
                                            whiteSpace: "nowrap",
                                            lineHeight: 1.4,
                                            // Match header responsive sizing
                                            minWidth: { xs: 100, sm: 120 },
                                            maxWidth: { xs: 140, sm: 160 },
                                            fontSize: { xs: '0.7rem', sm: '0.875rem' }, // Smaller text on mobile
                                        }}
                                    >
                                        {displayCell(col, row[col])}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}